import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../ui/accordion";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";

function Field({ id, label, value, onChange, type = "text", min, step, warning, placeholder, inputMode }) {
  return (
    <div>
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        type={type}
        min={min}
        step={step}
        inputMode={inputMode}
        value={value}
        onChange={(e) => onChange(id, e.target.value)}
        placeholder={placeholder}
        className="mt-1"
        aria-invalid={warning ? "true" : "false"}
        aria-describedby={warning ? `${id}-warning` : undefined}
      />
      {warning ? (
        <p id={`${id}-warning`} className="mt-1 text-xs text-amber-700">
          {warning}
        </p>
      ) : null}
    </div>
  );
}

function TextField({ id, label, value, onChange, warning, placeholder, rows = 4 }) {
  return (
    <div>
      <Label htmlFor={id}>{label}</Label>
      <Textarea
        id={id}
        value={value}
        onChange={(e) => onChange(id, e.target.value)}
        placeholder={placeholder}
        className="mt-1"
        rows={rows}
        aria-invalid={warning ? "true" : "false"}
        aria-describedby={warning ? `${id}-warning` : undefined}
      />
      {warning ? (
        <p id={`${id}-warning`} className="mt-1 text-xs text-amber-700">
          {warning}
        </p>
      ) : null}
    </div>
  );
}

export default function BusinessCaseForm({ values, setValue, warnings }) {
  return (
    <section className="rounded-xl border border-border bg-card p-5">
      <h2 className="font-display font-bold text-xl text-ink">Employer assumptions</h2>
      <p className="mt-2 text-sm text-muted-foreground">
        Calculations are completed within your browser. Information entered into this tool is not submitted to Railway Careers UK unless a future server-side saving feature is explicitly introduced.
      </p>

      <Accordion type="multiple" className="mt-4 w-full" defaultValue={["org", "funnels", "time", "salary", "value", "narrative"]}>
        <AccordionItem value="org">
          <AccordionTrigger>1. Organisation and decision</AccordionTrigger>
          <AccordionContent>
            <div className="grid md:grid-cols-2 gap-4">
              <Field id="companyName" label="Company or business unit" value={values.companyName} onChange={setValue} />
              <Field id="campaignName" label="Recruitment campaign or role" value={values.campaignName} onChange={setValue} />
              <Field id="preparedBy" label="Prepared by" value={values.preparedBy} onChange={setValue} />
              <Field id="targetDecisionDate" label="Target decision date" type="date" value={values.targetDecisionDate} onChange={setValue} />
              <div className="md:col-span-2">
                <TextField id="currentRecruitmentPressure" label="Current recruitment problem or pressure" value={values.currentRecruitmentPressure} onChange={setValue} rows={4} />
              </div>
              <div className="md:col-span-2">
                <TextField id="decisionRequested" label="Decision or approval requested" value={values.decisionRequested} onChange={setValue} rows={3} />
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="funnels">
          <AccordionTrigger>2. Recruitment funnels</AccordionTrigger>
          <AccordionContent>
            <div className="grid md:grid-cols-2 gap-5">
              <div className="rounded-lg border border-border p-4">
                <h3 className="font-semibold text-ink">Conventional recruitment campaign</h3>
                <div className="mt-3 grid gap-3">
                  <Field id="conventionalApplicationsReceived" label="Applications received" type="number" min="0" value={values.conventionalApplicationsReceived} onChange={setValue} warning={warnings.conventionalApplicationsReceived} />
                  <Field id="conventionalGatewayPass" label="Applications passing automatic gateway" type="number" min="0" value={values.conventionalGatewayPass} onChange={setValue} warning={warnings.conventionalGatewayPass} />
                  <Field id="conventionalOperationalShortlist" label="Candidates sent to operational shortlisting" type="number" min="0" value={values.conventionalOperationalShortlist} onChange={setValue} warning={warnings.conventionalOperationalShortlist} />
                  <Field id="conventionalInterviewed" label="Candidates interviewed" type="number" min="0" value={values.conventionalInterviewed} onChange={setValue} warning={warnings.conventionalInterviewed} />
                  <Field id="appointmentsRequired" label="Appointments required" type="number" min="1" value={values.appointmentsRequired} onChange={setValue} warning={warnings.appointmentsRequired} />
                  <Field id="campaignsPerYear" label="Comparable campaigns per year" type="number" min="0" step="1" value={values.campaignsPerYear} onChange={setValue} warning={warnings.campaignsPerYear} />
                </div>
              </div>

              <div className="rounded-lg border border-border p-4">
                <h3 className="font-semibold text-ink">Railway Careers UK Talent Pool route</h3>
                <div className="mt-3 grid gap-3">
                  <Field id="talentProfilesReviewed" label="Talent-pool profiles reviewed" type="number" min="0" value={values.talentProfilesReviewed} onChange={setValue} warning={warnings.talentProfilesReviewed} />
                  <Field id="talentOperationalShortlist" label="Profiles sent to operational shortlisting" type="number" min="0" value={values.talentOperationalShortlist} onChange={setValue} warning={warnings.talentOperationalShortlist} />
                  <Field id="talentInterviewed" label="Candidates interviewed" type="number" min="0" value={values.talentInterviewed} onChange={setValue} warning={warnings.talentInterviewed} />
                  <Field id="talentRouteSetupHours" label="Talent-pool route setup hours" type="number" min="0" step="0.25" value={values.talentRouteSetupHours} onChange={setValue} />
                  <Field id="talentFixedSchedulingHours" label="Talent-pool fixed scheduling setup hours" type="number" min="0" step="0.25" value={values.talentFixedSchedulingHours} onChange={setValue} warning={warnings.talentFixedSchedulingHours} />
                </div>
              </div>
            </div>
            <div className="mt-4 rounded-lg border border-amber/40 bg-amber/10 p-3 text-sm text-ink">
              Railway Careers UK does not replace employer eligibility verification, right-to-work checks, formal company selection, recognised train-driver psychometric assessments, safety-critical medical examinations, drug and alcohol screening, references or the employer's final appointment decision.
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="time">
          <AccordionTrigger>3. Time and panel assumptions</AccordionTrigger>
          <AccordionContent>
            <div className="grid md:grid-cols-2 gap-5">
              <div className="rounded-lg border border-border p-4">
                <h3 className="font-semibold text-ink">Recruitment-team activity</h3>
                <div className="mt-3 grid gap-3">
                  <Field id="gatewaySetupHours" label="Automatic gateway setup and exception handling (hours)" type="number" min="0" step="0.25" value={values.gatewaySetupHours} onChange={setValue} />
                  <Field id="conventionalReviewMinutes" label="Human review per conventional application (minutes)" type="number" min="0" step="0.5" value={values.conventionalReviewMinutes} onChange={setValue} />
                  <Field id="talentReviewMinutes" label="Review per talent-pool profile (minutes)" type="number" min="0" step="0.5" value={values.talentReviewMinutes} onChange={setValue} />
                  <Field id="fixedSchedulingHours" label="Fixed interview scheduling setup (hours)" type="number" min="0" step="0.25" value={values.fixedSchedulingHours} onChange={setValue} />
                  <Field id="schedulingMinutesPerInterview" label="Scheduling and correspondence per interviewee (minutes)" type="number" min="0" step="0.5" value={values.schedulingMinutesPerInterview} onChange={setValue} />
                  <Field id="offerProgressionHours" label="Offer and progression administration per appointee (hours)" type="number" min="0" step="0.25" value={values.offerProgressionHours} onChange={setValue} />
                </div>
              </div>
              <div className="rounded-lg border border-border p-4">
                <h3 className="font-semibold text-ink">Operational activity</h3>
                <div className="mt-3 grid gap-3">
                  <Field id="conventionalOpsShortlistHours" label="Conventional operational shortlist (hours)" type="number" min="0" step="0.25" value={values.conventionalOpsShortlistHours} onChange={setValue} />
                  <Field id="talentOpsShortlistHours" label="Talent-pool operational shortlist (hours)" type="number" min="0" step="0.25" value={values.talentOpsShortlistHours} onChange={setValue} />
                  <Field id="candidatesPerInterviewDay" label="Candidates interviewed per day" type="number" min="1" step="1" value={values.candidatesPerInterviewDay} onChange={setValue} warning={warnings.candidatesPerInterviewDay} />
                  <Field id="interviewDayHours" label="Interview day length (hours)" type="number" min="0.5" step="0.25" value={values.interviewDayHours} onChange={setValue} />
                  <Field id="hrPanelMembers" label="Recruitment or HR panel members" type="number" min="0" step="1" value={values.hrPanelMembers} onChange={setValue} />
                  <Field id="operationalPanelMembers" label="Operational panel members" type="number" min="0" step="1" value={values.operationalPanelMembers} onChange={setValue} />
                </div>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="salary">
          <AccordionTrigger>4. Salaries and loaded hourly rates</AccordionTrigger>
          <AccordionContent>
            <div className="grid md:grid-cols-2 gap-4">
              <Field id="hrAnnualSalary" label="Recruitment or HR salary (£)" type="number" min="0" step="100" value={values.hrAnnualSalary} onChange={setValue} />
              <Field id="operationalAnnualSalary" label="Operational manager salary (£)" type="number" min="0" step="100" value={values.operationalAnnualSalary} onChange={setValue} />
              <Field id="weeklyContractedHours" label="Contracted hours per week" type="number" min="0" step="0.1" value={values.weeklyContractedHours} onChange={setValue} />
              <Field id="productiveWeeksPerYear" label="Productive weeks per year" type="number" min="0" max="52" step="0.1" value={values.productiveWeeksPerYear} onChange={setValue} warning={warnings.productiveWeeksPerYear} />
              <Field id="employerNiRatePct" label="Employer National Insurance rate (%)" type="number" min="0" step="0.1" value={values.employerNiRatePct} onChange={setValue} />
              <Field id="employerNiThreshold" label="Employer National Insurance threshold (£)" type="number" min="0" step="100" value={values.employerNiThreshold} onChange={setValue} />
              <Field id="employerPensionRatePct" label="Employer pension contribution (%)" type="number" min="0" step="0.1" value={values.employerPensionRatePct} onChange={setValue} />
              <Field id="additionalOverheadPct" label="Additional employment overhead (%)" type="number" min="0" step="0.1" value={values.additionalOverheadPct} onChange={setValue} />
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="value">
          <AccordionTrigger>5. Talent Pool commercial inputs</AccordionTrigger>
          <AccordionContent>
            <div className="grid md:grid-cols-2 gap-4">
              <Field id="successfulRailwayHires" label="Expected successful Talent Pool hires" type="number" min="0" step="1" value={values.successfulRailwayHires} onChange={setValue} warning={warnings.successfulRailwayHires} />
              <Field id="feePerSuccessfulHire" label="Fee per successful Talent Pool hire (£)" type="number" min="0" step="1" value={values.feePerSuccessfulHire} onChange={setValue} />
              <Field id="talentPoolAccessCostPerCampaign" label="Optional implementation or onboarding cost per campaign (£)" type="number" min="0" step="1" value={values.talentPoolAccessCostPerCampaign} onChange={setValue} />
            </div>
            <p className="mt-3 text-xs text-muted-foreground">
              The calculator now prioritises internal hours saved and indicative internal value against the Talent Pool fee. Leave optional implementation cost at zero unless it applies.
            </p>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="narrative">
          <AccordionTrigger>6. Benefits, risks and recommendation</AccordionTrigger>
          <AccordionContent>
            <div className="grid gap-4">
              <TextField
                id="additionalExpectedBenefits"
                label="Additional expected benefits"
                value={values.additionalExpectedBenefits}
                onChange={setValue}
                placeholder="Example: release recruitment and operations capacity for other safety-critical projects."
              />
              <TextField
                id="risksAndControls"
                label="Risks and controls"
                value={values.risksAndControls}
                onChange={setValue}
                placeholder="Example: pilot assumptions may overstate early savings. Control with weekly review and benchmark comparison."
              />
              <TextField
                id="recommendedPilotScope"
                label="Recommended pilot scope"
                value={values.recommendedPilotScope}
                onChange={setValue}
                placeholder="Example: one campaign, one role family, 8 to 12 week cycle, stage-by-stage KPI review."
              />
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </section>
  );
}
